import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box, Stack, Typography, Card, Chip, Tabs, Tab, Button, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, IconButton, Fab,
  useTheme, useMediaQuery
} from "@mui/material";
import PlayArrow from "@mui/icons-material/PlayArrow";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import StopRounded from "@mui/icons-material/StopRounded";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import SmartToy from "@mui/icons-material/SmartToy";
import Add from "@mui/icons-material/Add";
import { useState } from "react";
import { LineChart } from "@mui/x-charts/LineChart";
import { BOTS, ACCOUNTS } from "@/data/mock-data";
import { formatUSD } from "@/lib/formatters";
import { DeltaBadge } from "@/components/DeltaBadge";
import { CircularActionButton } from "@/components/CircularActionButton";
import { CreateMenu, BOT_CREATE_ACTIONS } from "@/components/CreateMenu";
import { MetricTip } from "@/components/MetricTip";
import { TERMS } from "@/lib/terms";
import { chartColors } from "@/theme/palette";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

export function BotDetailPage() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [tab, setTab] = useState(0);
  const [confirmStop, setConfirmStop] = useState(false);
  // Shared Create menu anchor. In bot context, Order/Add money/Withdraw
  // target this bot (Add money = proportional rebalance-in; Withdraw =
  // proportional rebalance-out). See memory `project_create_actions_scope`.
  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);

  const bot = BOTS.find((b) => b.id === botId);
  if (!bot) {
    return (
      <Box textAlign="center" py={8}>
        <Typography>Bot not found.</Typography>
        <Button component={Link} to="/bots" sx={{ mt: 2 }}>← Back to bots</Button>
      </Box>
    );
  }

  const account = ACCOUNTS.find((a) => a.id === bot.accountId);
  const equityDates = bot.sparkline.map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (bot.sparkline.length - 1 - i));
    return d;
  });

  return (
    <Box sx={{ maxWidth: 760, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
        onClick={() => navigate("/bots")}
        size="small"
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Bots
      </Button>

      {/* Hero */}
      <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: "rgba(38,211,154,0.16)", color: "primary.main",
            display: "grid", placeItems: "center"
          }}>
            <SmartToy />
          </Box>
          <Typography variant="h2" sx={{ fontSize: "1.75rem !important" }}>{bot.name}</Typography>
          {/* Desktop inline (+) — mobile uses the FAB below so the hero
           *  title stays centred and uncluttered on small screens. */}
          {isDesktop && (
            <IconButton
              onClick={(e) => setCreateAnchor(e.currentTarget)}
              aria-label="Create"
              sx={{
                ml: 0.5,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.light" }
              }}
            >
              <Add />
            </IconButton>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {account?.name} {account?.masked}
        </Typography>
        <Chip
          label={bot.status}
          size="small"
          color={bot.status === "ACTIVE" ? "success" : "default"}
          sx={{ fontSize: "0.6875rem", letterSpacing: "0.1em" }}
        />

        <Typography variant="overline" color="text.secondary" sx={{ mt: 2 }}>
          Total Portfolio Value
        </Typography>
        <Typography variant="h1" sx={{ fontFeatureSettings: '"tnum" 1' }}>
          {formatUSD(bot.currentValue)}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MetricTip term={TERMS.dollarReturn}>
            <span>
              <DeltaBadge value={bot.allTimeGainPct} suffix="all time" variant="chip" />
            </span>
          </MetricTip>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: bot.allTimeGain >= 0 ? "primary.main" : "error.main",
              fontFeatureSettings: '"tnum" 1'
            }}
          >
            {bot.allTimeGain >= 0 ? "+" : ""}{formatUSD(bot.allTimeGain)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Bot buying power <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
              {formatUSD(bot.buyingPower)}
            </Box>
          </Typography>
          <MetricTip term={TERMS.buyingPower}>
            <IconButton size="small" sx={{ p: 0.25, color: "text.disabled" }} aria-label="About buying power">
              <InfoOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </MetricTip>
        </Stack>
      </Stack>

      {/* Action row */}
      <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 4 }}>
        <CircularActionButton icon={PlayArrow}       label="Go Live" />
        <CircularActionButton icon={HistoryRounded}  label="Orders" />
        <CircularActionButton icon={StopRounded}     label="Stop" danger onClick={() => setConfirmStop(true)} />
        <CircularActionButton icon={MoreHoriz}       label="More" />
      </Stack>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons={false} sx={{ mb: 2 }}>
        <Tab label="Performance" />
        <Tab label="Holdings" />
        <Tab label="Orders" />
        <Tab label="Settings" />
      </Tabs>

      {tab === 0 && (
        <Card sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">Portfolio Value</Typography>
          <Typography variant="h3" sx={{ fontFeatureSettings: '"tnum" 1', mb: 2 }}>
            {formatUSD(bot.currentValue)}
          </Typography>
          <LineChart
            height={260}
            margin={{ top: 10, bottom: 30, left: 60, right: 10 }}
            xAxis={[{
              data: equityDates,
              scaleType: "time",
              valueFormatter: (v: Date) => v.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
              tickLabelStyle: { fill: theme.palette.text.secondary, fontSize: 11 }
            }]}
            yAxis={[{
              tickLabelStyle: { fill: theme.palette.text.secondary, fontSize: 11 },
              valueFormatter: (v: number) =>
                new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v)
            }]}
            series={[{
              data: bot.sparkline,
              color: bot.performancePct >= 0 ? chartColors.primary : chartColors.negative,
              showMark: false,
              area: true,
              curve: "monotoneX"
            }]}
            grid={{ horizontal: true }}
            sx={{
              "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: theme.palette.divider },
              "& .MuiChartsGrid-line": { stroke: chartColors.grid, strokeDasharray: "4 4" }
            }}
          />
        </Card>
      )}

      {tab === 1 && <PlaceholderCard label="Holdings" text="21 positions — MSFT (8.2%), AAPL (6.7%), NVDA (5.9%), …" />}
      {tab === 2 && <PlaceholderCard label="Orders" text="Last 30 days · 42 filled orders · 0 errors" />}
      {tab === 3 && <PlaceholderCard label="Settings" text={bot.description} />}

      <Dialog open={confirmStop} onClose={() => setConfirmStop(false)}>
        <DialogTitle>Stop this bot?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Active positions will be held but no new trades will be placed until you resume the bot.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStop(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => setConfirmStop(false)}>Stop bot</Button>
        </DialogActions>
      </Dialog>

      {/* Mobile-only FAB — desktop (+) lives inline next to the bot name. */}
      {!isDesktop && (
        <Fab
          color="primary"
          aria-label="Create"
          onClick={(e) => setCreateAnchor(e.currentTarget)}
          sx={{
            position: "fixed",
            right: 16,
            bottom: "calc(80px + env(safe-area-inset-bottom))"
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Bot-scoped Create menu — only Add money / Withdraw, both meaning
       *  a proportional rebalance on this bot's holdings.
       *  Create / Order / Broker / etc. don't belong here; they live on the
       *  sidebar CreateButton and AccountsPage (+). */}
      <CreateMenu
        anchor={createAnchor}
        onClose={() => setCreateAnchor(null)}
        actions={BOT_CREATE_ACTIONS}
        anchorOrigin={{ vertical: isDesktop ? "bottom" : "top", horizontal: "right" }}
        transformOrigin={{ vertical: isDesktop ? "top" : "bottom", horizontal: "right" }}
      />
    </Box>
  );
}

function PlaceholderCard({ label, text }: { label: string; text: string }) {
  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="body1" sx={{ mt: 1 }}>{text}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
        Full {label.toLowerCase()} view ships in the next iteration.
      </Typography>
    </Card>
  );
}
